import { forwardRef, useCallback, useEffect, useState } from 'react';
import { html } from './chartHtml';
import { modalities } from '../config';
import { createActiveDoghnutConfig, getChartConfig } from '../helper';
import { isPatientApp } from '@/config/constants';
import { logError } from '@/utils/logHandling';

const DICTIONARY = {
  'Verlauf Mündliches Benennen': 0,
  'Verlauf Auditives Sprachverständnis': 1,
  'Verlauf Schriftliches Benennen': 2,
  'Verlauf Lesesinnverständnis': 3,
  showWortset: ' Ergebnisse einzelner Wörter anzeigen',
  showSatzset: ' Ergebnisse einzelner Sätze anzeigen',
  hideWortset: '  Ergebnisse einzelner Wörter verbergen',
  hideSatzset: '  Ergebnisse einzelner Sätze verbergen',
  Satzset: 'Sätzeset',
  Wortset: 'Wörterset',
};

const Web = forwardRef(function Web(
  {
    webViewTitle,
    appDisplayName,
    setType,
    individualStatsData,
    generatedTimeSeries,
  },
  ref,
) {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const sendMessage = useCallback((data) => {
    ref.current.contentWindow.postMessage(JSON.stringify(data), '*');
  }, []);

  useEffect(() => {
    try {
      if (!isIframeLoaded) {
        return;
      }
      const highChartConfigs = [];
      const KEYS = Object.keys(generatedTimeSeries);
      KEYS.forEach((key) => {
        if (key === 'Overview') {
          return null;
        }
        let isEmpty = false;
        const series = generatedTimeSeries[key];
        if (series.length === 1) {
          const { data } = series[0];
          isEmpty = data.every((item) => {
            return item[1] === 0;
          });
        }
        const name = modalities[key];
        const chartOptions = getChartConfig(series, name);
        const index = DICTIONARY[chartOptions?.title?.text] ?? 0;
        highChartConfigs[index] = { isEmpty, chartOptions };
      });

      let overAllData = null;
      const dataArray = [];
      // Removing the item inside the last index since it's the overview of the charts which is already rendered individually on the top of the page
      individualStatsData.forEach((item, index) => {
        if (index === individualStatsData.length - 1) {
          const allData = createActiveDoghnutConfig(item, true);
          overAllData = allData;
          return;
        }
        const { print, thumbnail_url } = item;
        const {
          understandingConfig,
          speakingConfig,
          writingConfig,
          readingConfig,
        } = createActiveDoghnutConfig(item, false);
        const individualDataArray = [
          speakingConfig,
          understandingConfig,
          writingConfig,
          readingConfig,
        ];
        dataArray.push({ print, thumbnail_url, individualDataArray });
      });

      const {
        speakingConfig,
        understandingConfig,
        writingConfig,
        readingConfig,
      } = overAllData;

      const isSentence = setType === 'Satzset';
      const title = `Ergebnisse ${DICTIONARY[setType]} <b>${appDisplayName}</b> (${webViewTitle})`;
      const showText = DICTIONARY[`show${setType}`];
      const hideText = DICTIONARY[`hide${setType}`];

      const data = {
        speakingConfig,
        understandingConfig,
        writingConfig,
        readingConfig,
        highChartConfigs,
        individualStats: dataArray,
        title,
        showText,
        hideText,
        isSentence,
        hidePrint: isPatientApp,
      };
      sendMessage(data);
    } catch (error) {
      logError('', error);
    }
  }, [isIframeLoaded, individualStatsData, generatedTimeSeries]);

  return (
    <iframe
      ref={ref}
      title="web-statistics-page"
      id="web-statistics-page"
      srcDoc={html}
      frameBorder="0"
      height="100%"
      width="100%"
      allowFullScreen
      onLoad={() => {
        setIsIframeLoaded(true);
      }}
    />
  );
});

export default Web;
